<template>
    <div class="my-3">
        <div class="w-full lg:w-11/12 mx-auto">
            <h1 class="text-center text-3xl">Calendario de Entregas</h1>

            <p class="mt-3">
                Por defecto el sistema consulta las ordenes en curso, es decir
                una orden que esta en un estado de entrega diferente al
                entregado o cancelado.
            </p>
        </div>

        <div class="flex md:hidden justify-center mt-6 flex-col items-center">
            <h3 class="capitalize">
                {{ currentMonth }}
            </h3>
            <span class="text-lg">
                Ordenes encontradas
                <span class="font-bold text-theme-primary">
                    {{ itemsNumber }}
                </span>
            </span>
        </div>
        <div class="flex justify-around mt-4 md:mt-2">
            <button class="btn btn-stack-primary" @click="previousMonth">
                Mes anterior
            </button>
            <div class="hidden md:flex flex-col items-center">
                <h3 class="capitalize text-lg">
                    {{ currentMonth }}
                </h3>
                <span class="text-md">
                    Ordenes encontradas
                    <span class="font-bold">
                        {{ itemsNumber }}
                    </span>
                </span>
            </div>

            <button class="btn btn-stack-primary" @click="nextMonth">
                Mes siguiente
            </button>
        </div>
        <div class="flex justify-center mt-4 mb-5">
            <order-delivery-calendar
                :items-by-date="ordersGroupByDeliveryAt"
                :start-date="startDate"
                :end-date="endDate"
                @day-click="handleDayClick"
            />
        </div>
        <app-dialog v-model="isOpenDetailsModal" size="full">
            <template #header>
                <div class="flex w-full mx-4">
                    <div class="w-2/12"></div>
                    <div class="w-8/12 flex flex-col items-center">
                        <h2 class="text-2xl">
                            {{ selected.length > 1 ? "Ordenes" : "Orden" }}
                            para entregar el
                            <span class="text-theme-primary">
                                {{ dateSelected }}
                            </span>
                        </h2>
                        <h3 class="text-lg">Total {{ selected.length }}</h3>
                    </div>
                    <div class="w-2/12 flex justify-center">
                        <app-button
                            :loading="isGeneratingPDF"
                            @click="downloadPDFComponent"
                        >
                            📄 PDF
                        </app-button>
                    </div>
                </div>
            </template>
            <div class="space-y-2">
                <div v-for="order in selected" :key="order.id">
                    <a
                        :href="`/app/orders/detail/${order.id}`"
                        target="_blank"
                        rel="noopener"
                        class="text-theme-primary text-2xl text-center hover:underline"
                    >
                        Orden #{{ order.id }}
                    </a>
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-4 my-3">
                        <div
                            v-if="order.customer"
                            class="col-span-full border-b pb-2 mb-2"
                        >
                            <strong>Datos del cliente</strong>
                            <div
                                class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1"
                            >
                                <div class="flex">
                                    <strong>Nombre:</strong>
                                    <span class="ml-1"
                                        >{{ order.customer.firstName }}
                                        {{ order.customer.lastName }}</span
                                    >
                                </div>
                                <div class="flex">
                                    <strong>Teléfono:</strong>
                                    <span class="ml-1">{{
                                        order.customer.phone
                                    }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col">
                            <strong>Estado</strong>
                            <span
                                :class="
                                    $data.$ordersStatusesMessageClass[
                                        order.deliveryStatus
                                    ]
                                "
                            >
                                {{ order.deliveryStatusMessage }}
                            </span>
                        </div>

                        <div class="flex flex-col">
                            <strong>Costo de envío</strong>
                            <span>{{ order.deliveryPrice | money }}</span>
                        </div>

                        <div class="flex flex-col">
                            <strong>Total</strong>
                            <span>{{ order.total | money }}</span>
                        </div>

                        <div
                            v-if="order.total !== order.valuePaid"
                            class="flex flex-col"
                        >
                            <strong>Abono</strong>
                            <span>{{ order.valuePaid | money }}</span>
                        </div>

                        <div class="flex flex-col">
                            <strong>Estado del pago</strong>
                            <span
                                :class="
                                    $data.$paymentStatusesMessageClass[
                                        order.paymentStatus
                                    ]
                                "
                            >
                                {{ order.paymentStatusMessage }}
                            </span>
                        </div>

                        <div class="flex flex-col">
                            <strong>Fecha de creación</strong>
                            <span>{{ order.createdAt | date }}</span>
                        </div>

                        <div class="flex flex-col">
                            <strong>Fecha de entrega</strong>
                            <span>{{ order.deliveryAt | dateSimple }}</span>
                        </div>
                    </div>
                    <order-reference-table-item
                        v-for="(reference, index) in order.references"
                        :key="index"
                        :reference="reference"
                        :enabled-details="false"
                        class="w-full mx-auto mt-1"
                    />
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions, mapGetters } from "vuex";

import { paymentStatusesMessageClass } from "@/common/fields/orders-payment-status-codes";
import { ordersStatusesMessageClass } from "@/common/fields/orders-status-codes";
import { generateOrderDetailedListPDF } from "@/common/lib/pdf";

import OrderDeliveryCalendar from "@/components/orders/OrderDeliveryCalendar.vue";
import OrderReferenceTableItem from "@/components/orders/OrderReferenceTableItem.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "CalendarOrders",
    components: { AppDialog, OrderDeliveryCalendar, OrderReferenceTableItem },
    layout: "app",
    meta: {
        roles: ["administrator", "support"]
    },
    data: () => ({
        $ordersStatusesMessageClass: ordersStatusesMessageClass,
        $paymentStatusesMessageClass: paymentStatusesMessageClass,
        ordersGroupByDeliveryAt: {},
        selected: [],
        itemsNumber: 0,
        currentMonth: "Enero",
        startDate: "2025-02-01",
        endDate: "2023-02-28",
        dateSelected: "",
        isOpenDetailsModal: false,
        isGeneratingPDF: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("order:List", "/app"))) return;
        const now = DateTime.local();
        this.currentMonth = now.toLocaleString({
            month: "long",
            year: "numeric"
        });
        this.startDate = now.startOf("month").toISODate();
        this.endDate = now.endOf("month").toISODate();
        this.listItems();
    },
    methods: {
        ...mapActions("orders", ["listOrdersSchedule"]),
        async listItems() {
            this.ordersGroupByDeliveryAt = {};
            this.selected = [];
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items } = await this.listOrdersSchedule({
                    begin: this.startDate,
                    end: this.endDate
                });
                let objectAux = {};
                items.forEach((item) => {
                    const deliveryAt = DateTime.fromISO(item.deliveryAt);
                    const deliveryAtFormat = deliveryAt.toISODate();
                    if (!(deliveryAtFormat in objectAux)) {
                        objectAux[deliveryAtFormat] = [item];
                    } else {
                        objectAux[deliveryAtFormat].push(item);
                    }
                });
                this.ordersGroupByDeliveryAt = objectAux;
                this.itemsNumber = items.length;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            await this.listItems();
        },
        handleDayClick(date) {
            this.dateSelected = date;
            this.selected = this.ordersGroupByDeliveryAt[date];
            this.isOpenDetailsModal = true;
        },
        async previousMonth() {
            const newStartDate = DateTime.fromISO(this.startDate).minus({
                months: 1
            });
            this.currentMonth = newStartDate.toLocaleString({
                month: "long",
                year: "numeric"
            });
            this.startDate = newStartDate.toISODate();
            this.endDate = newStartDate.endOf("month").toISODate();
            await this.listItems();
        },
        async nextMonth() {
            const newStartDate = DateTime.fromISO(this.startDate).plus({
                months: 1
            });
            this.currentMonth = newStartDate.toLocaleString({
                month: "long",
                year: "numeric"
            });
            this.startDate = newStartDate.toISODate();
            this.endDate = newStartDate.endOf("month").toISODate();
            await this.listItems();
        },
        async downloadPDFComponent() {
            this.isGeneratingPDF = true;
            try {
                await generateOrderDetailedListPDF(
                    this.selected,
                    this.dateSelected
                );
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Error al generar PDF",
                    text: error.message || "No se pudo generar el archivo PDF",
                    icon: "error"
                });
            } finally {
                this.isGeneratingPDF = false;
            }
        }
    }
};
</script>
